import React, { useContext, Suspense, lazy, useEffect } from "react";

// Third-party
import { Route } from "react-router-dom";

// Material UI
import CssBaseline from "@material-ui/core/CssBaseline";

// Context
import Map2dProvider from "context/Map2dContext";
import { AuthContext } from "AuthProvider";
import ProcessProvider from "ProcessProvider";
import ModalProvider from "ModalProvider";
import PaletteProvider from "context/PaletteContext";
import IdentifyProvider from "context/IdentifyContext";
import ProductFruitsProvider from "context/ProductFruitsContext";
import UiProvider from "context/UiContext";
import AllResults from "context/AllResults";
import GoogleAnalyticsProvider from "context/GoogleAnalyticsContext";
import GoogleMapsProvider from "context/GoogleMapsProvider";

// Hooks
import useThreads from "hooks/useThreads";

// Components
import CookieConsentBanner from "features/Utils/Cookies/CookieConsentBanner/CookieConsentBanner";
import LiveChat from "features/Utils/LiveChat/LiveChat";

// Constants
const Dashboard2D = lazy(() => import("components/Dashboard2D/Dashboard2D"));
const Dashboard3D = lazy(() => import("components/Dashboard3D"));
const Login = lazy(() => import("features/Authorization/Login"));
const TwoFactorVerification = lazy(
  () => import("features/Authorization/TwoFactorVerification")
);

// Utils
import getenvvar from "utils/Env";

// Other
import "./App.css";

const LiveChat_License = getenvvar("REACT_APP_LIVECHAT_LICENSE");

/**
 * Main application entry point.
 *
 * @returns React.ReactElement
 */
function App() {
  const authContext = useContext(AuthContext);

  const threads = useThreads();

  useEffect(() => {
    window.CESIUM_BASE_URL = "./Cesium";
  }, []);

  function onLiveChatStateChanged(e) {
    authContext.setChatState(e.state);
  }

  function onLiveChatLoaded() {
    authContext.setChatLoaded(true);
  }

  if (!authContext.loading) {
    if (authContext.isAuthenticated) {
      return (
        <Suspense fallback={<></>}>
          <ProductFruitsProvider
            isAuthenticated
            profileId={authContext.profileId}
            userType={authContext.userType}
            userName={authContext.userName}
            companyId={authContext.companyId}
            companySubscription={authContext.salesData?.company_subscription}
            companyGeography={authContext.salesData?.company_geography}
            hmacCode={authContext.hmacCode}
          >
            <GoogleMapsProvider>
              <GoogleAnalyticsProvider>
                <Route path="/">
                  <UiProvider>
                    <LiveChat
                      license={LiveChat_License}
                      onChatLoaded={onLiveChatLoaded}
                      onChatStateChanged={onLiveChatStateChanged}
                    />

                    <IdentifyProvider>
                      <PaletteProvider>
                        <ModalProvider>
                          <ProcessProvider>
                            <AllResults>
                              <CookieConsentBanner />

                              <CssBaseline />

                              <div className="content pa0 bg-black h-100 w-100">
                                <Map2dProvider threads={threads}>
                                  <Route
                                    exact
                                    path="/"
                                    component={Dashboard2D}
                                  />
                                </Map2dProvider>
                                <Route
                                  exact
                                  path="/3d"
                                  component={Dashboard3D}
                                />
                              </div>
                            </AllResults>
                          </ProcessProvider>
                        </ModalProvider>
                      </PaletteProvider>
                    </IdentifyProvider>
                  </UiProvider>
                </Route>
              </GoogleAnalyticsProvider>
            </GoogleMapsProvider>
          </ProductFruitsProvider>
        </Suspense>
      );
    } else {
      return (
        <div className="content pa0 h-100 w-100">
          <Suspense fallback={<></>}>
            <Route>
              <Route exact path="/" component={props => <Login {...props} />} />

              <Route
                exact
                path="/verify"
                component={props => <TwoFactorVerification {...props} />}
              />
            </Route>
          </Suspense>
        </div>
      );
    }
  } else {
    return <div></div>;
  }
}

export default App;
